import { useState, useEffect } from 'react';
import { AnchorAPI } from '../modules/AnchorAPI';
import { FeatureFlag } from '../modules/FeatureFlags';
import { localStorage } from '../modules/Browser/localStorage';

const FEATURE_FLAG_KEY = 'featureFlags';
type FeatureFlags = FeatureFlag[];

/**
 * Sets feature flags in browser storage. If you pass an argument, it will set
 * that value as the feature flag. Otherwise, it will fetch the current user's
 * feature flags and sets them in browser storage
 * @param newFeatureFlags
 */
async function setFeatureFlagsIndexedDb(newFeatureFlags?: FeatureFlags | null) {
  try {
    let featureFlags = newFeatureFlags;
    let error;
    if (featureFlags === undefined) {
      const {
        json,
        error: fetchError,
      } = await AnchorAPI.fetchAllEnabledFeatureFlags();
      if (json) {
        featureFlags = json.allEnabledFlags.map(({ name }) => name);
      }
      error = fetchError;
    }
    localStorage.setItem(FEATURE_FLAG_KEY, featureFlags);
    return { error, featureFlags };
  } catch (err) {
    throw new Error(err.message);
  }
}

function useFeatureFlags() {
  const [featureFlags, setFeatureFlags] = useState<
    FeatureFlags | null | undefined
  >(undefined);
  const [error, setError] = useState<string | null>(null);

  async function handleSetFeatureFlags() {
    try {
      setError(null);
      const {
        error: newError,
        featureFlags: newFeatureFlags,
      } = await setFeatureFlagsIndexedDb();
      if (newFeatureFlags) setFeatureFlags(newFeatureFlags);
      if (newError) {
        setError(newError);
      }
    } catch (err) {
      setError(err.message);
    }
  }

  useEffect(() => {
    localStorage.getItem(FEATURE_FLAG_KEY).then(featureFlagsStored => {
      setFeatureFlags(featureFlagsStored || null);
    });
  }, []);

  useEffect(() => {
    if (featureFlags === null) {
      handleSetFeatureFlags();
    } else if (featureFlags !== undefined) {
      setFeatureFlagsIndexedDb(featureFlags);
    }
  }, [featureFlags]);

  return { featureFlags, error, handleSetFeatureFlags };
}

export { useFeatureFlags, setFeatureFlagsIndexedDb, FEATURE_FLAG_KEY };
